import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { Rocket, CalendarDays, ClipboardList, Unlock, Settings, User, Bell, Clock, Smartphone, Scale, Lightbulb, AlertTriangle } from 'lucide-react';

/* ─── Shared UI Primitives ──────────────────────────────────────────────────── */

function MockUI({ children, style }) {
  return (
    <div style={{
      border: '1.5px solid var(--border, #e2e8f0)', borderRadius: 10,
      padding: 16, background: 'var(--bg-subtle, #f8fafc)',
      display: 'block', position: 'relative', maxWidth: '100%', overflowX: 'auto',
      ...style,
    }}>
      {children}
    </div>
  );
}

function ClickArrow({ label = 'Click here' }) {
  return (
    <div style={{
      position: 'absolute', top: -30, left: '50%', transform: 'translateX(-50%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', pointerEvents: 'none',
    }}>
      <div style={{ background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4, whiteSpace: 'nowrap', marginBottom: 2 }}>{label}</div>
      <div style={{ width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '8px solid #ef4444' }} />
    </div>
  );
}

function Tip({ children }) {
  return (
    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#1e40af', marginTop: 12, lineHeight: 1.7, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
      <Lightbulb size={15} style={{ flexShrink: 0, marginTop: 2 }} />
      <span><strong>Tip:</strong> {children}</span>
    </div>
  );
}

function Warning({ children }) {
  return (
    <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#9a3412', marginTop: 12, lineHeight: 1.7, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
      <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 2 }} />
      <span><strong>Note:</strong> {children}</span>
    </div>
  );
}

function SubList({ items }) {
  return (
    <div style={{ marginTop: 8 }}>
      {items.map((item, i) => (
        <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 8, alignItems: 'flex-start' }}>
          <div style={{ minWidth: 22, height: 22, background: '#e2e8f0', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 11, color: '#475569', flexShrink: 0, marginTop: 1 }}>→</div>
          <div style={{ fontSize: 14, color: 'var(--text, #0f172a)', lineHeight: 1.75 }}>{item}</div>
        </div>
      ))}
    </div>
  );
}

function SectionHeader({ icon, title, subtitle }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '40px 0 24px', paddingBottom: 12, borderBottom: '2px solid var(--border, #e2e8f0)' }}>
      <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--primary, #2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#fff' }}>{icon}</div>
      <div>
        <div style={{ fontWeight: 800, fontSize: 18, color: 'var(--text, #0f172a)' }}>{title}</div>
        {subtitle && <div style={{ fontSize: 13, color: 'var(--text-muted, #64748b)', marginTop: 2 }}>{subtitle}</div>}
      </div>
    </div>
  );
}

function Step({ number, title, children, visual }) {
  return (
    <div className="help-step">
      <div className="help-step-num">{number}</div>
      <div className="help-step-body">
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 10 }}>{title}</div>
        <div style={{ color: 'var(--text-muted, #64748b)', fontSize: 14, lineHeight: 1.8 }}>{children}</div>
        {visual && <div style={{ marginTop: 36, paddingTop: 4 }}>{visual}</div>}
      </div>
    </div>
  );
}

/* ─── Mock UI Components ────────────────────────────────────────────────────── */

function MockLoginForm({ isStaff }) {
  return (
    <MockUI>
      <div style={{ width: 240 }}>
        {isStaff && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Restaurant name</div>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 5, padding: '6px 10px', fontSize: 13, background: '#fff' }}>e.g. wakarimasita</div>
          </div>
        )}
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Email</div>
          <div style={{ border: '1px solid #e2e8f0', borderRadius: 5, padding: '6px 10px', fontSize: 13, background: '#fff' }}>your@email.com</div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Password</div>
          <div style={{ border: '1px solid #e2e8f0', borderRadius: 5, padding: '6px 10px', fontSize: 13, background: '#fff' }}>••••••••</div>
        </div>
        <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
          <div style={{ background: '#2563eb', color: '#fff', padding: '8px 0', borderRadius: 7, fontWeight: 600, fontSize: 14, textAlign: 'center' }}>Log in</div>
          <ClickArrow />
        </div>
      </div>
    </MockUI>
  );
}

function MockRegister() {
  return (
    <MockUI>
      <div style={{ width: 260 }}>
        {[['Restaurant name', 'e.g. Sunrise Cafe'], ['Your email', 'you@email.com'], ['Password', '••••••••']].map(([label, placeholder]) => (
          <div key={label} style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>{label}</div>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 5, padding: '6px 10px', fontSize: 13, background: '#fff' }}>{placeholder}</div>
          </div>
        ))}
        <div style={{ position: 'relative', display: 'inline-block', width: '100%', marginTop: 4 }}>
          <div style={{ background: '#2563eb', color: '#fff', padding: '8px 0', borderRadius: 7, fontWeight: 600, fontSize: 14, textAlign: 'center' }}>Create Account</div>
          <ClickArrow />
        </div>
      </div>
    </MockUI>
  );
}

function MockSettingsNav() {
  const sections = ['Business Info', 'Logo', 'Roles & Skills', 'Staffing Rules', 'Weather', 'Events', 'Change Password'];
  return (
    <MockUI style={{ maxWidth: 340 }}>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>Settings page — sections inside:</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {sections.map(s => (
          <div key={s} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, padding: '5px 10px', fontSize: 12, color: '#0f172a', fontWeight: 500 }}>{s}</div>
        ))}
      </div>
    </MockUI>
  );
}

function MockRoleChips() {
  return (
    <MockUI style={{ maxWidth: 340 }}>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>Your custom roles (tap + to add more):</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        {['Kitchen', 'Front of House', 'Barista', 'Manager'].map(r => (
          <div key={r} style={{ background: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd', borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 600 }}>{r} ✕</div>
        ))}
        <div style={{ position: 'relative' }}>
          <div style={{ background: '#2563eb', color: '#fff', borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 600 }}>+ Add Role</div>
          <ClickArrow />
        </div>
      </div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>Skills:</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {['Barista', 'Food Prep', 'POS System', 'Cash Handling'].map(s => (
          <div key={s} style={{ background: '#f0fdf4', color: '#166534', border: '1px solid #86efac', borderRadius: 20, padding: '4px 10px', fontSize: 11 }}>{s}</div>
        ))}
        <div style={{ background: '#16a34a', color: '#fff', borderRadius: 20, padding: '4px 10px', fontSize: 11, fontWeight: 600 }}>+ Add Skill</div>
      </div>
    </MockUI>
  );
}

function MockStaffingRule() {
  return (
    <MockUI style={{ maxWidth: 360 }}>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>Staffing rules — set per role, per day:</div>
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 7, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '120px 60px 60px 80px 80px', gap: 0, fontSize: 11, fontWeight: 600, color: '#64748b', background: '#f8fafc', padding: '6px 10px', borderBottom: '1px solid #e2e8f0' }}>
          <span>Role</span><span>Day</span><span>Min</span><span>Max staff</span><span>Open–Close</span>
        </div>
        {[
          ['Kitchen', 'Mon', '2', '4', '8am–10pm'],
          ['Front of House', 'Mon', '1', '3', '9am–9pm'],
        ].map(([role, day, min, max, hrs]) => (
          <div key={role} style={{ display: 'grid', gridTemplateColumns: '120px 60px 60px 80px 80px', gap: 0, fontSize: 12, padding: '6px 10px', borderBottom: '1px solid #f1f5f9' }}>
            <span style={{ fontWeight: 600 }}>{role}</span>
            <span>{day}</span>
            <span>{min}</span>
            <span>{max}</span>
            <span style={{ color: '#16a34a' }}>{hrs}</span>
          </div>
        ))}
      </div>
    </MockUI>
  );
}

function MockEmployeeForm() {
  return (
    <MockUI style={{ maxWidth: 340 }}>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>Add Employee form — all fields:</div>
      {[
        ['Name *', 'Amy Chen'],
        ['Email', 'amy@email.com'],
        ['Password', '(create one for them)'],
        ['Role *', 'Kitchen ▾'],
        ['Employment type', 'Casual ▾'],
        ['Award-exempt', '☐ flat-rate/cash pay'],
        ['Min hours/week', '0'],
        ['Max hours/week', '38'],
        ['Hourly rate ($)', '25.00'],
      ].map(([label, val]) => (
        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 11, color: '#64748b', minWidth: 130 }}>{label}</span>
          <div style={{ border: '1px solid #e2e8f0', borderRadius: 5, padding: '4px 8px', fontSize: 12, background: '#fff', minWidth: 100, color: '#0f172a' }}>{val}</div>
        </div>
      ))}
      <div style={{ marginTop: 6 }}>
        <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Skills (tick all that apply):</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {['Barista ✓', 'Food Prep', 'POS System ✓'].map(s => (
            <div key={s} style={{ background: s.includes('✓') ? '#dbeafe' : '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 4, padding: '3px 8px', fontSize: 11 }}>{s}</div>
          ))}
        </div>
      </div>
      <div style={{ position: 'relative', marginTop: 12 }}>
        <div style={{ background: '#2563eb', color: '#fff', padding: '7px 0', borderRadius: 6, fontWeight: 600, fontSize: 13, textAlign: 'center' }}>Save Employee</div>
        <ClickArrow />
      </div>
    </MockUI>
  );
}

function MockAvailabilityGrid() {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const available = [0, 1, 3, 4]; // Mon Tue Thu Fri
  return (
    <MockUI style={{ maxWidth: 380 }}>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>Weekly availability — tap each day to toggle:</div>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 12 }}>
        {days.map((d, i) => (
          <div key={d} style={{
            padding: '6px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            background: available.includes(i) ? '#2563eb' : '#f1f5f9',
            color: available.includes(i) ? '#fff' : '#94a3b8',
            border: `1px solid ${available.includes(i) ? '#2563eb' : '#e2e8f0'}`,
          }}>{d}</div>
        ))}
      </div>
      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>For each available day, set your hours:</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 12 }}>Start:</div>
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 5, padding: '4px 8px', fontSize: 12, background: '#fff' }}>09:00</div>
        <div style={{ fontSize: 12 }}>End:</div>
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 5, padding: '4px 8px', fontSize: 12, background: '#fff' }}>17:00</div>
      </div>
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <div style={{ background: '#2563eb', color: '#fff', padding: '6px 18px', borderRadius: 6, fontSize: 12, fontWeight: 600 }}>Save Availability</div>
        <ClickArrow />
      </div>
    </MockUI>
  );
}

function MockDateOverride() {
  return (
    <MockUI style={{ maxWidth: 300 }}>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>Add a date override — for one specific day:</div>
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Date</div>
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 5, padding: '6px 10px', fontSize: 12, background: '#fff' }}>2025-08-15</div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600, color: '#dc2626' }}>❌ Not available</div>
        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600, color: '#16a34a' }}>✓ Available</div>
      </div>
      <div style={{ fontSize: 11, color: '#64748b' }}>Reason (optional): "Holiday" / "Sick day" etc.</div>
    </MockUI>
  );
}

function MockRosterActions() {
  return (
    <MockUI style={{ padding: '37px 16px 16px 16px' }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative' }}>
          <div style={{ background: '#7c3aed', color: '#fff', padding: '7px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600 }}>✨ Generate Roster</div>
          <ClickArrow />
        </div>
        <div style={{ fontSize: 13, color: '#64748b' }}>→ review → then</div>
        <div style={{ background: '#16a34a', color: '#fff', padding: '7px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600 }}>Publish</div>
      </div>
    </MockUI>
  );
}

function MockShiftCard() {
  return (
    <MockUI>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>Click any shift card on the Roster page:</div>
      <div style={{ display: 'flex', gap: 6 }}>
        <div style={{ position: 'relative' }}>
          <div style={{ background: '#dcfce7', border: '2px solid #16a34a', borderRadius: 5, padding: '8px 12px', fontSize: 12, cursor: 'pointer' }}>
            <div style={{ fontWeight: 600 }}>Amy Chen</div>
            <div style={{ fontSize: 11, color: '#16a34a' }}>9am–5pm · Kitchen</div>
          </div>
          <ClickArrow />
        </div>
        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 5, padding: '8px 12px', fontSize: 12 }}>
          <div style={{ fontWeight: 600 }}>Ryan Park</div>
          <div style={{ fontSize: 11, color: '#16a34a' }}>10am–6pm · FOH</div>
        </div>
      </div>
      <div style={{ marginTop: 10, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 12px', fontSize: 12 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Popup options:</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {['Edit Shift', 'Open Shift', 'Delete'].map(o => (
            <div key={o} style={{ background: o === 'Open Shift' ? '#7c3aed' : '#f1f5f9', color: o === 'Open Shift' ? '#fff' : '#475569', padding: '4px 10px', borderRadius: 5, fontSize: 11, fontWeight: 600 }}>{o}</div>
          ))}
        </div>
      </div>
    </MockUI>
  );
}

function MockApprove() {
  return (
    <MockUI style={{ maxWidth: 280 }}>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>Shift card with a pending request:</div>
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 7, padding: '10px 14px' }}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Kitchen · 10am–6pm</div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>Requested by: Amy Chen</div>
        <div style={{ display: 'flex', gap: 6 }}>
          <div style={{ position: 'relative' }}>
            <div style={{ background: '#16a34a', color: '#fff', padding: '5px 10px', borderRadius: 5, fontSize: 12, fontWeight: 600 }}>Approve ✓</div>
            <ClickArrow />
          </div>
          <div style={{ background: '#e2e8f0', color: '#64748b', padding: '5px 10px', borderRadius: 5, fontSize: 12, fontWeight: 600 }}>Reject</div>
        </div>
      </div>
    </MockUI>
  );
}

function MockVersionPanel() {
  return (
    <MockUI style={{ maxWidth: 340 }}>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>Versions panel (click "Versions" button on Roster page):</div>
      {[
        { ver: 'v3', label: 'Published', date: 'Today 2:15pm', color: '#16a34a' },
        { ver: 'v2', label: 'AI Draft', date: 'Today 2:00pm', color: '#7c3aed' },
        { ver: 'v1', label: 'Manual Save', date: 'Yesterday', color: '#2563eb' },
      ].map(({ ver, label, date, color }) => (
        <div key={ver} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, marginBottom: 6 }}>
          <div>
            <span style={{ background: color, color: '#fff', borderRadius: 4, padding: '2px 6px', fontSize: 10, fontWeight: 700, marginRight: 6 }}>{ver}</span>
            <span style={{ fontSize: 12, fontWeight: 600 }}>{label}</span>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>{date}</div>
          </div>
          <div style={{ background: '#f1f5f9', color: '#475569', padding: '4px 8px', borderRadius: 5, fontSize: 11, fontWeight: 600 }}>Restore</div>
        </div>
      ))}
      <div style={{ position: 'relative', display: 'inline-block', marginTop: 4 }}>
        <div style={{ background: '#2563eb', color: '#fff', padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600 }}>💾 Save Version</div>
        <ClickArrow />
      </div>
    </MockUI>
  );
}

function MockAuditLog() {
  return (
    <MockUI style={{ maxWidth: 400 }}>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>Audit Log page — record of every change:</div>
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 7, overflow: 'hidden' }}>
        {[
          { action: 'Roster Published', by: 'You', detail: 'Week of 12 Apr · 18 shifts', color: '#16a34a' },
          { action: 'Shift Opened', by: 'You', detail: 'Kitchen · 10am–6pm', color: '#d97706' },
          { action: 'Request Approved', by: 'You', detail: 'Amy Chen', color: '#16a34a' },
        ].map(({ action, by, detail, color }, i) => (
          <div key={i} style={{ display: 'flex', gap: 10, padding: '8px 12px', borderBottom: '1px solid #f1f5f9', alignItems: 'center' }}>
            <span style={{ background: color, color: '#fff', borderRadius: 4, padding: '2px 7px', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>{action}</span>
            <span style={{ fontSize: 11, color: '#64748b' }}>{detail} · by {by}</span>
          </div>
        ))}
      </div>
    </MockUI>
  );
}

function MockBell() {
  return (
    <MockUI style={{ padding: '37px 16px 16px 30px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
          </svg>
          <span style={{ position: 'absolute', top: -4, right: -4, background: '#ef4444', color: '#fff', borderRadius: '50%', fontSize: 9, fontWeight: 700, width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>3</span>
          <div style={{ position: 'absolute', top: -28, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4, whiteSpace: 'nowrap', marginBottom: 2 }}>Click here</div>
            <div style={{ width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '8px solid #ef4444' }} />
          </div>
        </div>
        <span style={{ fontSize: 13, color: '#64748b' }}>Top right corner</span>
      </div>
    </MockUI>
  );
}

function MockOpenShift() {
  return (
    <MockUI style={{ maxWidth: 260 }}>
      <div style={{ background: '#fff', border: '1px solid #fbbf24', borderRadius: 7, padding: '10px 14px' }}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>Kitchen</div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>Sun 12 Apr · 10am–6pm</div>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <div style={{ background: '#2563eb', color: '#fff', padding: '5px 12px', borderRadius: 5, fontSize: 12, fontWeight: 600 }}>Request Shift</div>
          <ClickArrow />
        </div>
      </div>
    </MockUI>
  );
}

function MockWeatherSettings() {
  return (
    <MockUI style={{ maxWidth: 360 }}>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>Weather adjustments — tell the AI how weather affects your business:</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {[
          { w: '🌧️ Rain', adj: '−1 staff' },
          { w: '☀️ Sunny', adj: '+2 staff' },
          { w: '🌡️ Hot', adj: '+1 staff' },
          { w: '⛈️ Storm', adj: '−2 staff' },
        ].map(({ w, adj }) => (
          <div key={w} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, padding: '5px 10px', fontSize: 12 }}>
            <span>{w}</span>
            <span style={{ color: adj.startsWith('+') ? '#16a34a' : '#dc2626', fontWeight: 700 }}>{adj}</span>
          </div>
        ))}
      </div>
    </MockUI>
  );
}

function MockAccountPage() {
  return (
    <MockUI style={{ maxWidth: 300 }}>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>Account page (staff only):</div>
      {[['Name', 'Amy Chen'], ['Email', 'amy@email.com']].map(([l, v]) => (
        <div key={l} style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>{l}</div>
          <div style={{ border: '1px solid #e2e8f0', borderRadius: 5, padding: '6px 10px', fontSize: 13, background: '#fff' }}>{v}</div>
        </div>
      ))}
      <div style={{ position: 'relative', display: 'inline-block', marginTop: 4 }}>
        <div style={{ background: '#2563eb', color: '#fff', padding: '6px 18px', borderRadius: 6, fontSize: 12, fontWeight: 600 }}>Save Changes</div>
        <ClickArrow />
      </div>
    </MockUI>
  );
}

/* ─── Step Content ──────────────────────────────────────────────────────────── */

const OWNER_CONTENT = (
  <>
    {/* PART A: First-time Setup */}
    <SectionHeader icon={<Rocket size={20} />} title="Part 1: First-Time Setup" subtitle="Do these steps once when you first start using the app." />

    <Step number={1} title="Create your account (first time only)"
      visual={<MockRegister />}>
      <SubList items={[
        <>Open your browser (Safari, Chrome, etc.) and tap the address bar at the very top of the screen — the long bar that shows a web address.</>,
        <>Type: <strong>roster-app.pages.dev/register</strong> and press <strong>Enter</strong> / tap <strong>Go</strong>.</>,
        <>You will see a "Create Account" page. Fill in: <strong>Restaurant name</strong> (this becomes your unique ID — e.g. "sunrisecafe" — no spaces, no capitals), <strong>Your email</strong>, and a <strong>Password</strong> (at least 8 characters).</>,
        <>Tap the blue <strong>Create Account</strong> button. You will be logged in automatically.</>,
      ]} />
      <Warning>Choose your restaurant name carefully — it cannot be changed later. Staff will need this name to log in.</Warning>
    </Step>

    <Step number={2} title="Log in (every time after that)"
      visual={<MockLoginForm />}>
      <SubList items={[
        <>Go to <strong>roster-app.pages.dev/login</strong> in your browser.</>,
        <>Enter your email and password, then tap <strong>Log in</strong>.</>,
        <>You will be taken straight to your Dashboard.</>,
      ]} />
      <Tip>Your browser can save your password — tap "Save" when it asks. That way you never have to type it again.</Tip>
    </Step>

    <Step number={3} title="Set up your business settings (very important — do this first!)"
      visual={<MockSettingsNav />}>
      <SubList items={[
        <>Look at the top navigation bar and tap <strong>Settings</strong>.</>,
        <>This page has several sections. Go through each one:</>,
      ]} />
      <div style={{ marginTop: 10, marginLeft: 32 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6, color: '#0f172a' }}>📍 Business Info</div>
        <SubList items={[
          <>Set your <strong>Business name</strong> (the name customers/staff see), <strong>Location</strong>, and <strong>Timezone</strong> (choose your Australian state, e.g. Australia/Sydney).</>,
          <>Tap <strong>Save</strong> at the bottom of that section.</>,
        ]} />
        <div style={{ fontWeight: 700, fontSize: 13, margin: '12px 0 6px', color: '#0f172a' }}>🖼️ Logo</div>
        <SubList items={[
          <>Tap <strong>Upload Logo</strong> to add your restaurant logo. It will appear in the top-left corner of the app.</>,
          <>Maximum size is 500KB. Tap Save after uploading.</>,
        ]} />
      </div>
    </Step>

    <Step number={4} title="Create your custom roles and skills"
      visual={<MockRoleChips />}>
      <SubList items={[
        <>In Settings, scroll to the <strong>Roles & Skills</strong> section.</>,
        <>Tap <strong>+ Add Role</strong> and type your job titles — e.g. "Kitchen", "Front of House", "Barista", "Manager".</>,
        <>Press <strong>Enter</strong> to save each role. You can add as many as you need.</>,
        <>In the Skills section below, tap <strong>+ Add Skill</strong> for specific abilities — e.g. "Barista", "Food Prep", "POS System", "Cash Handling", "Wine Service".</>,
        <>Tap <strong>Save</strong>. These roles and skills will now be available when you add employees.</>,
      ]} />
      <Tip>Roles are job positions (what someone does). Skills are specific abilities they have. A "Front of House" staff member might have "Wine Service" and "POS System" skills.</Tip>
    </Step>

    <Step number={5} title="Set staffing rules (tells the AI how many people you need)"
      visual={<MockStaffingRule />}>
      <SubList items={[
        <>In Settings, scroll to the <strong>Staffing Rules</strong> section.</>,
        <>For each role and each day of the week, you can set:</>
      ]} />
      <SubList items={[
        <><strong>Min staff</strong> — the minimum number of people you need in that role on that day</>,
        <><strong>Max staff</strong> — the maximum (won't schedule more than this)</>,
        <><strong>Opening time</strong> — what time the earliest shift starts</>,
        <><strong>Closing time</strong> — what time the latest shift ends</>,
      ]} />
      <SubList items={[
        <>For example: Kitchen on Monday — Min 2, Max 4, Open 8am–10pm.</>,
        <>Tap <strong>Save</strong> when done. The AI uses these rules when building the roster.</>,
      ]} />
    </Step>

    <Step number={6} title="Set up weather adjustments (optional but useful)"
      visual={<MockWeatherSettings />}>
      <SubList items={[
        <>In Settings, scroll to <strong>Weather Adjustments</strong>.</>,
        <>For each weather condition (Rain, Sunny, Hot, Cold, Storm, Cloudy), set how it affects how many staff you need.</>,
        <>Example: "Rainy days = −1 staff" (fewer customers), "Sunny days = +2 staff" (busier).</>,
        <>The AI checks the weather forecast and adjusts the roster automatically.</>,
        <>Tap <strong>Save</strong>.</>,
      ]} />
    </Step>

    <Step number={7} title="Set up event adjustments (optional but useful)">
      <SubList items={[
        <>In Settings, scroll to <strong>Event-Based Adjustments</strong> and tap <strong>"+ Add Adjustment"</strong>.</>,
        <>Select an event from the dropdown. There are two groups:</>,
      ]} />
      <SubList items={[
        <><strong>Special Days</strong> — automatically detected each year: Mother's Day, Father's Day, Easter Week, Valentine's Day, Melbourne Cup, Lunar New Year, Ramadan, and more. Each one shows the date for the current year.</>,
        <><strong>Nearby Events (PredictHQ)</strong> — concerts, sports matches, festivals, and conferences happening near your restaurant.</>,
      ]} />
      <SubList items={[
        <>Select the <strong>Role</strong> this adjustment applies to (e.g. Kitchen, Front of House).</>,
        <>Select the <strong>Time of Day</strong>: All Day, Morning (before 12pm), Lunch (12pm–5pm), or Dinner (5pm+). Use this if the event only affects one meal service — for example, Ramadan might boost dinner but not lunch.</>,
        <>Set the <strong>Staff Adjustment</strong>: positive to add staff, negative to reduce. Example: Mother's Day +2 Front of House.</>,
        <>Tap <strong>Save</strong>. You can add multiple rows for the same event — one for each role or time of day.</>,
      ]} />
      <Tip>For events like Ramadan or Lunar New Year where the busy period is dinner only, set Time of Day to "Dinner" so the adjustment only applies to dinner shifts.</Tip>
    </Step>

    <Step number={8} title="Add your staff members"
      visual={<MockEmployeeForm />}>
      <SubList items={[
        <>Tap <strong>Employees</strong> in the top navigation bar.</>,
        <>Tap the <strong>"+ Add Employee"</strong> button in the top-right corner of the page.</>,
        <>A form will appear. Fill in every field:</>,
      ]} />
      <SubList items={[
        <><strong>Name *</strong> — their full name</>,
        <><strong>Email</strong> — their email address (they use this to log in)</>,
        <><strong>Password</strong> — create a password for them. Write it down and give it to them personally</>,
        <><strong>Role *</strong> — select their job role from your list (e.g. Kitchen)</>,
        <><strong>Employment type</strong> — Casual, Part-time, or Full-time</>,
        <><strong>Award-exempt</strong> — tick this if they are on a flat rate or cash pay (not covered by the restaurant award). Overtime and minimum-hours warnings will be suppressed for them.</>,
        <><strong>Min hours/week</strong> — how few hours they can work (usually 0 for casuals)</>,
        <><strong>Max hours/week</strong> — the most hours they can work per week (e.g. 38)</>,
        <><strong>Hourly rate ($)</strong> — their pay rate (used to calculate estimated wages on the roster)</>,
        <><strong>Skills</strong> — tick the skills they have</>,
        <><strong>Notes</strong> — any notes about this person (e.g. "Cannot work Sundays")</>,
      ]} />
      <SubList items={[
        <>Tap <strong>Save Employee</strong>. Repeat this for every staff member.</>,
        <>Once added, they can log in at <strong>roster-app.pages.dev/staff/login</strong> using the restaurant name, their email, and the password you set.</>,
      ]} />
      <Warning>Give each staff member their login details — restaurant name, email, and password. They need all three to log in.</Warning>
    </Step>

    {/* PART B: Availability */}
    <SectionHeader icon={<CalendarDays size={20} />} title="Part 2: Managing Availability" subtitle="Know when your staff can work before building the roster." />

    <Step number={9} title="Ask staff to set their availability (recommended)"
      visual={<MockAvailabilityGrid />}>
      <SubList items={[
        <>Ask each staff member to log in to the app and tap <strong>Availability</strong> in their top menu.</>,
        <>Staff can set which days they are available each week, and the times they can work each day.</>,
        <>They can also add <strong>date overrides</strong> for specific days — e.g. "I cannot work on 15 August".</>,
        <>You will see all staff availability on the <strong>Availability</strong> page in your own account.</>,
      ]} />
      <Tip>The more accurately staff update their availability, the better the AI-generated roster will be.</Tip>
    </Step>

    <Step number={10} title="Set availability on behalf of a staff member (if they struggle with the app)"
      visual={<MockDateOverride />}>
      <SubList items={[
        <>Go to <strong>Availability</strong> in your top menu.</>,
        <>You will see a dropdown at the top — select a staff member's name.</>,
        <>You can then set or edit their weekly availability and date overrides on their behalf.</>,
        <>Tap <strong>Save</strong> when done.</>,
      ]} />
    </Step>

    {/* PART C: Building the Roster */}
    <SectionHeader icon={<ClipboardList size={20} />} title="Part 3: Building the Roster" subtitle="Generate, edit, and publish the weekly schedule." />

    <Step number={11} title="Generate the roster with AI"
      visual={<MockRosterActions />}>
      <SubList items={[
        <>Tap <strong>Roster</strong> in the top navigation bar.</>,
        <>At the top of the page, use the <strong>week picker</strong> (← → arrows) to navigate to the week you want to schedule.</>,
        <>Tap the purple <strong>"✨ Generate Roster"</strong> button.</>,
        <>Wait a few seconds. The AI will automatically create a full schedule that considers:</>,
      ]} />
      <SubList items={[
        <>Each staff member's weekly availability</>,
        <>Your staffing rules (min/max per role, opening/closing times)</>,
        <>Weather forecast for that week</>,
        <>Local events that might affect how busy you are</>,
        <>Australian Fair Work Act rules (breaks, penalties, max hours)</>,
      ]} />
      <SubList items={[
        <>The roster will appear as a grid — each column is a day, each row is a staff member.</>,
      ]} />
    </Step>

    <Step number={12} title="Review and edit the generated roster"
      visual={<MockShiftCard />}>
      <SubList items={[
        <>Scroll through the generated roster and check every shift.</>,
        <>Tap any <strong>shift card</strong> (the coloured box with a name on it) to open a popup with options:</>,
      ]} />
      <SubList items={[
        <><strong>Edit Shift</strong> — change the start time, end time, or role for this shift</>,
        <><strong>Lock Shift</strong> — freeze this shift so AI will never overwrite it when regenerating. A padlock icon appears on the card.</>,
        <><strong>Mark as Open</strong> — convert this shift so any available staff can request it</>,
        <><strong>Delete</strong> — remove this shift entirely</>,
      ]} />
      <SubList items={[
        <>To <strong>add a new shift manually</strong>: tap an empty cell in the grid and fill in the details.</>,
        <>Yellow warning icons on shifts mean there is a potential issue (e.g. overtime, penalty rates). Tap the icon to see the warning.</>,
        <>The total estimated wage cost for the week is shown at the top right of the roster.</>,
      ]} />
      <Tip><strong>Have staff with a fixed, pre-agreed schedule?</strong> That's exactly what Lock Shift is for. If Sarah always works Monday 9am–3pm by agreement, manually add that shift and lock it. When you hit Generate, the AI fills in everyone else and leaves Sarah's Monday exactly as it is. Think of it as telling the AI: "this one is already decided — work around it."</Tip>
      <Tip>The AI draft is a starting point — it's normal to make a few manual tweaks before publishing.</Tip>
    </Step>

    <Step number={13} title="Publish the roster">
      <SubList items={[
        <>When you are happy with the roster, tap the green <strong>Publish</strong> button at the top of the Roster page.</>,
        <>Staff will immediately be able to see their shifts when they log in.</>,
        <>Published rosters are marked with a green "Published" badge.</>,
        <>You can still edit a published roster — just make changes and publish again.</>,
      ]} />
    </Step>

    {/* PART D: Open Shifts */}
    <SectionHeader icon={<Unlock size={20} />} title="Part 4: Open Shifts" subtitle="Let staff pick up extra shifts or cover absences." />

    <Step number={14} title="Someone called in sick — unlock and open the shift"
      visual={<MockShiftCard />}>
      <SubList items={[
        <>Go to the <strong>Roster</strong> page.</>,
        <>If the roster is <strong>Locked</strong>, tap <strong>"Unlock Roster"</strong> at the top right to allow edits.</>,
        <>Tap the shift card of the person who cannot make it.</>,
        <>In the popup, tap <strong>"Open Shift"</strong> — this turns the shift orange and makes it available for staff to request.</>,
        <>Staff in that role are notified automatically and can see the open slot on their roster.</>,
      ]} />
      <Tip>After fixing the roster, you can Lock it again to prevent further accidental changes.</Tip>
    </Step>

    <Step number={15} title="Approve or reject a shift request"
      visual={<MockApprove />}>
      <SubList items={[
        <>When a staff member requests an open shift, you get a <strong>bell notification</strong> instantly.</>,
        <>Tap <strong>Open Shifts</strong> in the top navigation bar to see all requests.</>,
        <>If a staff member has requested a shift, their name appears on the shift card with two buttons:</>,
      ]} />
      <SubList items={[
        <><strong>Approve ✓</strong> — the shift is assigned to them. They get a notification. The shift is filled.</>,
        <><strong>Reject</strong> — their request is declined. The shift stays open for others to request.</>,
      ]} />
      <Tip>You can also see all requests under the Open Shifts page, filterable by status.</Tip>
    </Step>

    {/* PART E: Advanced */}
    <SectionHeader icon={<Settings size={20} />} title="Part 5: Advanced Features" subtitle="Versioning, audit logs, and other powerful tools." />

    <Step number={16} title="Save and restore roster versions"
      visual={<MockVersionPanel />}>
      <SubList items={[
        <>On the Roster page, tap the <strong>"Versions"</strong> button (next to the Publish button).</>,
        <>A panel slides open showing all saved versions of the roster for that week.</>,
        <>To save the current roster as a version: tap <strong>"💾 Save Version"</strong> and give it a name (e.g. "Before changes"). This creates a snapshot you can come back to.</>,
        <>Versions are also <strong>automatically saved</strong> when you:</>,
      ]} />
      <SubList items={[
        <>Generate a roster with AI (saved as "AI Draft")</>,
        <>Publish the roster (saved as "Published")</>,
      ]} />
      <SubList items={[
        <>To go back to an earlier version: tap the <strong>Restore</strong> button next to that version. The roster will revert to exactly that state.</>,
        <>Each version also shows the total estimated wage cost, so you can compare rosters by cost.</>,
      ]} />
      <Warning>You cannot restore a version if the roster is locked. Unlock it first.</Warning>
    </Step>

    <Step number={17} title="View the audit log (see the full history of all changes)"
      visual={<MockAuditLog />}>
      <SubList items={[
        <>Tap <strong>Audit Log</strong> in the top navigation bar.</>,
        <>This page shows a complete history of every change made to the roster and shifts — who did it and when.</>,
        <>Every action is recorded: shift created, shift updated, shift deleted, open shift posted, request approved/rejected, roster generated, roster published, roster restored.</>,
        <>Use the <strong>filter dropdown</strong> at the top to show only a specific type of action (e.g. only "Request Approved" events).</>,
        <>This is useful if there is a dispute about who changed a shift or when a roster was published.</>,
      ]} />
    </Step>

    <Step number={18} title="Check and manage notifications"
      visual={<MockBell />}>
      <SubList items={[
        <>Look at the top-right corner of the screen. Tap the <strong>bell icon</strong>.</>,
        <>A dropdown list appears showing all recent notifications.</>,
        <>Tap any notification to go to that page directly.</>,
        <>To mark individual notifications as read: tap the notification.</>,
        <>To clear all notifications at once: tap <strong>"Mark all read"</strong> at the top of the list.</>,
        <>Notifications appear when: a staff member requests an open shift, a roster is published, a shift is assigned.</>,
      ]} />
    </Step>

    <Step number={19} title="Change your password">
      <SubList items={[
        <>Tap <strong>Settings</strong> in the top navigation bar.</>,
        <>Scroll all the way to the bottom of the Settings page to find the <strong>Change Password</strong> section.</>,
        <>Enter your current password, then your new password twice.</>,
        <>New password must be at least 8 characters.</>,
        <>Tap <strong>Update Password</strong>.</>,
      ]} />
    </Step>
  </>
);

const STAFF_CONTENT = (
  <>
    <SectionHeader icon={<Rocket size={20} />} title="Part 1: Getting Started" subtitle="Log in and find your shifts." />

    <Step number={1} title="Log in to the app"
      visual={<MockLoginForm isStaff />}>
      <SubList items={[
        <>Open your web browser on your phone or computer (Safari on iPhone, Chrome on Android).</>,
        <>Tap the <strong>address bar</strong> at the very top of the browser — the long bar showing a web address.</>,
        <>Type: <strong>roster-app.pages.dev/staff/login</strong> and tap <strong>Go</strong> or press <strong>Enter</strong>.</>,
        <>You will see a login page with THREE boxes. Fill in all three:</>,
      ]} />
      <SubList items={[
        <><strong>Restaurant name</strong> — the name your manager told you (e.g. "sunrisecafe"). Ask your manager if you're not sure.</>,
        <><strong>Email</strong> — your email address that your manager registered you with</>,
        <><strong>Password</strong> — the password your manager gave you when they set up your account</>,
      ]} />
      <SubList items={[
        <>Tap the blue <strong>Log in</strong> button. You will go to your Dashboard.</>,
      ]} />
      <Warning>If it says "Invalid credentials", check: (1) is CAPS LOCK off? (2) did you type the restaurant name exactly right? (3) is the email correct? Ask your manager to confirm your details.</Warning>
    </Step>

    <Step number={2} title="View your shifts (Dashboard)">
      <SubList items={[
        <>After logging in, you are on the <strong>Dashboard</strong>. It shows your shifts for this week at a glance.</>,
        <>Each shift shows the day, date, start time, end time, and your role (e.g. Kitchen 9am–5pm).</>,
        <>If you see "No shifts this week", your manager may not have published the roster yet — check back later.</>,
      ]} />
    </Step>

    <Step number={3} title="View the full roster (see everyone's schedule)">
      <SubList items={[
        <>Tap <strong>Roster</strong> in the top navigation bar.</>,
        <>You will see the weekly calendar with all staff shifts.</>,
        <><strong>Your own shifts</strong> are highlighted in a darker green so you can spot them easily.</>,
        <>Use the <strong>← →</strong> arrows next to the date at the top to see the previous or next week's roster.</>,
      ]} />
    </Step>

    <SectionHeader icon={<CalendarDays size={20} />} title="Part 2: Setting Your Availability" subtitle="Tell your manager when you can and can't work." />

    <Step number={4} title="Set your weekly availability (which days and times you can work)"
      visual={<MockAvailabilityGrid />}>
      <SubList items={[
        <>Tap <strong>Availability</strong> in the top navigation bar.</>,
        <>Tap the blue <strong>"Set Weekly Availability"</strong> button.</>,
        <>A grid of days appears. Tap each day you <strong>can</strong> work — it will turn blue when selected.</>,
        <>After selecting a day, set the times you are available for that day (start time and end time).</>,
        <>You can select multiple days at once by tapping each one you want.</>,
        <>Tap <strong>Save</strong> when you are done.</>,
        <>Your manager will see this when building the next roster.</>,
      ]} />
      <Tip>Update your availability at least a week before the next roster period. The earlier you update, the more likely you get the shifts you want.</Tip>
    </Step>

    <Step number={5} title="Add a date override (for a specific day you can't work)"
      visual={<MockDateOverride />}>
      <SubList items={[
        <>On the Availability page, look for the <strong>"Add Override"</strong> or <strong>"+ Add date override"</strong> button.</>,
        <>Select the specific date you cannot work (e.g. 15 August for a holiday).</>,
        <>Choose <strong>"Not available"</strong> for that day.</>,
        <>Optionally add a reason (e.g. "Holiday" or "Doctor appointment").</>,
        <>Tap <strong>Save</strong>. Your manager will see this override when building that week's roster.</>,
      ]} />
      <Tip>Date overrides override your regular weekly availability for that one specific day. Use them for one-off days off like holidays or appointments.</Tip>
    </Step>

    <SectionHeader icon={<Unlock size={20} />} title="Part 3: Open Shifts" subtitle="Pick up extra shifts that are available." />

    <Step number={6} title="See and request an open shift from the roster"
      visual={<MockOpenShift />}>
      <SubList items={[
        <>When a shift becomes available (e.g. a colleague called in sick), you get a <strong>bell notification</strong>.</>,
        <>Tap the notification or go to the <strong>Roster</strong> page.</>,
        <>Open shifts appear as <strong>orange highlighted</strong> cards in the roster grid — you can see exactly which day and role is missing.</>,
        <>Check your own shifts on the same week to make sure there's no conflict.</>,
        <>Tap the orange shift card → tap <strong>"Request This Shift"</strong>.</>,
        <>Your manager gets notified. They will approve or decline your request.</>,
        <>You get a bell notification when they decide.</>,
      ]} />
      <Tip>You can also find open shifts via the <strong>Open Shifts</strong> page in the top menu.</Tip>
    </Step>

    <Step number={7} title="Cancel a shift request">
      <SubList items={[
        <>If you change your mind before the manager approves, you can cancel it.</>,
        <>Go to <strong>Open Shifts</strong> in the top menu.</>,
        <>Find the shift — it will show a "Requested" badge.</>,
        <>Tap <strong>"Cancel Request"</strong>.</>,
        <>The shift goes back to open for others.</>,
      ]} />
    </Step>

    <SectionHeader icon={<User size={20} />} title="Part 4: Your Account" subtitle="Update your personal details and password." />

    <Step number={8} title="Change your name or email"
      visual={<MockAccountPage />}>
      <SubList items={[
        <>Tap <strong>Account</strong> in the top navigation bar (staff only — managers use Settings instead).</>,
        <>You will see a form with your current name and email address.</>,
        <>Tap on the Name or Email box and type your changes.</>,
        <>Tap <strong>Save Changes</strong>.</>,
      ]} />
    </Step>

    <Step number={9} title="Change your password">
      <SubList items={[
        <>On the Account page, scroll down to the <strong>Change Password</strong> section.</>,
        <>Type your <strong>current password</strong> in the first box.</>,
        <>Type your <strong>new password</strong> in the second box (at least 8 characters).</>,
        <>Type your new password <strong>again</strong> in the third box (to confirm there are no typos).</>,
        <>Tap <strong>Update Password</strong>.</>,
        <>If you forgot your current password, tap <a href="/forgot-password" style={{ color: '#2563eb' }}>Forgot Password</a> on the login page to reset it by email.</>,
      ]} />
    </Step>

    <SectionHeader icon={<Bell size={20} />} title="Part 5: Notifications" subtitle="Stay up to date on your shifts and requests." />

    <Step number={10} title="Check your notifications"
      visual={<MockBell />}>
      <SubList items={[
        <>Look at the top-right corner of the screen. You will see a <strong>bell icon</strong>.</>,
        <>If there is a <strong>red number badge</strong> on the bell (like "3"), it means you have that many unread notifications.</>,
        <>Tap the bell to open the notification list.</>,
        <>Notifications appear for things like:</>,
      ]} />
      <SubList items={[
        <>A new shift has been assigned to you (the roster was published)</>,
        <>Your shift request was approved — you got the shift!</>,
        <>Your shift request was rejected — the shift went to someone else</>,
        <>A new open shift is available that you might want to request</>,
      ]} />
      <SubList items={[
        <>Tap any notification to go straight to that page.</>,
        <>The notification turns grey once you have read it.</>,
      ]} />
      <Tip>Check your notifications every day so you don't miss any shift updates from your manager.</Tip>
    </Step>
  </>
);

/* ─── Main Page ─────────────────────────────────────────────────────────────── */

export default function HelpPage() {
  const { user } = useAuth();
  const isStaff = user?.role === 'staff';
  const [tab, setTab] = useState(isStaff ? 'staff' : 'owner');

  return (
    <div className="page-container">

      {/* Intro / Value proposition */}
      <div style={{
        background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)',
        borderRadius: 14, padding: '28px 28px', marginBottom: 36, color: '#fff',
      }}>
        <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 6 }}>RosterAI — Help Guide</div>
        <div style={{ fontSize: 15, opacity: 0.9, lineHeight: 1.7, marginBottom: 20 }}>
          RosterAI automatically builds your weekly staff schedule using AI. Instead of spending hours figuring out who works when, the AI does it in seconds — while following Australian Fair Work Act rules.
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {(tab === 'staff' ? [
            [<Smartphone size={18} />, 'Your schedule on your phone', 'Check shifts anywhere, any time'],
            [<Bell size={18} />, 'Get notified instantly', 'No more chasing your manager for updates'],
            [<CalendarDays size={18} />, 'Set your availability', 'Let your manager know when you\'re free'],
            [<Unlock size={18} />, 'Request open shifts', 'Pick up extra shifts with one tap'],
          ] : [
            [<Clock size={18} />, '130+ hours back a year', "That's $5,000+ in your time"],
            [<Scale size={18} />, 'Fair Work compliant', 'Overtime & penalty warnings built in'],
            [<Smartphone size={18} />, 'Works on any device', 'Desktop, tablet, or phone'],
            [<Bell size={18} />, 'Staff stay in the loop', 'Automated shift notifications'],
          ]).map(([icon, title, sub]) => (
            <div key={title} style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 10, padding: '12px 16px', minWidth: 140, flex: '1 1 140px' }}>
              <div style={{ marginBottom: 6, opacity: 0.95 }}>{icon}</div>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{title}</div>
              <div style={{ fontSize: 11, opacity: 0.8 }}>{sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Tab selector */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 8, border: '1.5px solid var(--border)', borderRadius: 10, overflow: 'hidden', width: 'fit-content' }}>
        {[{ key: 'owner', label: 'Manager / Owner' }, { key: 'staff', label: 'Staff' }].filter(({ key }) => !(isStaff && key === 'owner')).map(({ key, label }) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding: '12px 28px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 15,
            background: tab === key ? 'var(--primary, #2563eb)' : '#fff',
            color: tab === key ? '#fff' : 'var(--text-muted)',
            transition: 'all 0.15s',
          }}>{label}</button>
        ))}
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 32 }}>
        {tab === 'owner'
          ? 'You run the restaurant. You add staff, build the roster, and manage shifts.'
          : 'You work at the restaurant. You view your shifts, set availability, and request open shifts.'}
      </div>

      {/* Content */}
      {tab === 'owner' ? OWNER_CONTENT : STAFF_CONTENT}

      {/* CTA */}
      <div style={{ marginTop: 40, padding: '32px 28px', background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)', borderRadius: 14, textAlign: 'center', color: '#fff' }}>
        <div style={{ fontWeight: 800, fontSize: 20, marginBottom: 8 }}>
          {tab === 'owner' ? 'Ready to get started?' : 'Already have an account?'}
        </div>
        <div style={{ fontSize: 14, opacity: 0.85, marginBottom: 20 }}>
          {tab === 'owner'
            ? 'Create your restaurant and let AI build your first roster in minutes.'
            : 'Log in and check your shifts, availability, and open shift requests.'}
        </div>
        {tab === 'owner' ? (
          <Link to="/register" style={{ display: 'inline-block', background: '#fff', color: '#1e40af', fontWeight: 700, fontSize: 15, padding: '12px 32px', borderRadius: 8, textDecoration: 'none' }}>
            Create your restaurant →
          </Link>
        ) : (
          <Link to="/login?tab=staff" style={{ display: 'inline-block', background: '#fff', color: '#1e40af', fontWeight: 700, fontSize: 15, padding: '12px 32px', borderRadius: 8, textDecoration: 'none' }}>
            Log in as staff →
          </Link>
        )}
      </div>

      {/* Footer */}
      <div style={{ marginTop: 16, padding: '16px 20px', background: 'var(--bg-subtle, #f8fafc)', border: '1px solid var(--border)', borderRadius: 10, fontSize: 14, color: 'var(--text-muted)' }}>
        Forgot your password?{' '}
        <a href="/forgot-password" style={{ color: 'var(--primary)' }}>Reset it here.</a>
        <span style={{ margin: '0 8px', opacity: 0.4 }}>·</span>
        Still stuck? Contact your manager.
      </div>
    </div>
  );
}
